<template>
  <div v-if="!ballot" class="ballot-empty">
    <p>No ballot has been published for this meeting yet.</p>
  </div>
  <div v-else class="ballot-renderer">
    <component
      :is="componentFor(c.type)"
      v-for="c in ballot.components"
      :key="c.id"
      :component="c"
      :shareholder="shareholder"
      :mode="mode"
      :form="form"
      :share-value="shareValue"
      :ballot-components="ballot.components"
      :signature-ref="c.type === 'signature' ? signaturePadRef : null"
      @register-signature-pad="onRegisterSignaturePad"
      @update:field="onFieldUpdate"
    />

    <div v-if="mode === 'vote'" class="submit-row">
      <Button
        :label="submitting ? 'Submitting…' : 'Submit'"
        severity="primary"
        size="large"
        :loading="submitting"
        :disabled="submitting"
        @click="submit"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import TextView from './view/TextView.vue'
import ShareholderInfoView from './view/ShareholderInfoView.vue'
import BallotQuestionView from './view/BallotQuestionView.vue'
import BoxAView from './view/BoxAView.vue'
import BoxBView from './view/BoxBView.vue'
import TotalVotesView from './view/TotalVotesView.vue'
import SignatureView from './view/SignatureView.vue'
import CustodianView from './view/CustodianView.vue'
import WitnessesView from './view/WitnessesView.vue'
import DateView from './view/DateView.vue'

const props = defineProps({
  ballot: { type: Object, default: null },
  shareholder: { type: Object, default: () => ({}) },
  mode: { type: String, default: 'vote' }, // 'vote' | 'preview' | 'readonly'
  shareValue: { type: Number, default: 2 },
  existingAnswers: { type: Object, default: null },
  // Parent passes an async submit handler. We await it so the Submit button
  // can stay in its loading/disabled state for the entire upload+write flow.
  // A plain @submit emit wouldn't do that, because Vue's emit doesn't await
  // async listeners.
  onSubmit: { type: Function, default: null },
})

const emit = defineEmits(['submit'])
const toast = useToast()
const submitting = ref(false)

// form mirrors the shape stored on the votes doc:
// {
//   answers: { [questionId]: { choice: 'a'|'b', directed: { [candidateId]: number } } },
//   signcustodian, custodian, custodianshn, custodianShares,
//   sigx, witness1, witness2,
//   signatureDataUrl
// }
const form = reactive({
  answers: {},
  signcustodian: false,
  custodian: '',
  custodianshn: '',
  custodianShares: null,
  sigx: false,
  witness1: '',
  witness2: '',
  signatureDataUrl: null,
})

// Signature pad instance (populated by <SignatureView> when it mounts). We
// keep it on the parent so submit() can save + clear it without prop drilling.
const signaturePadRef = ref(null)

function componentFor(type) {
  switch (type) {
    case 'text':
      return TextView
    case 'shareholderInfo':
      return ShareholderInfoView
    case 'ballotQuestion':
      return BallotQuestionView
    case 'boxA':
      return BoxAView
    case 'boxB':
      return BoxBView
    case 'totalVotes':
      return TotalVotesView
    case 'signature':
      return SignatureView
    case 'custodian':
      return CustodianView
    case 'witnesses':
      return WitnessesView
    case 'date':
      return DateView
    default:
      return null
  }
}

// Initialize form.answers with zeroed-out directed values for every question
// in the ballot so v-model bindings have stable paths.
watch(
  () => props.ballot,
  (b) => {
    if (!b) return
    for (const c of b.components) {
      if (c.type === 'ballotQuestion') {
        if (!form.answers[c.id]) {
          form.answers[c.id] = { choice: null, directed: {} }
        }
        for (const cand of c.props?.candidates || []) {
          if (!(cand.id in form.answers[c.id].directed)) {
            form.answers[c.id].directed[cand.id] = 0
          }
        }
      }
    }
    if (props.existingAnswers) {
      for (const [qid, ans] of Object.entries(props.existingAnswers)) {
        form.answers[qid] = {
          choice: ans.choice || null,
          directed: { ...(ans.directed || {}) },
        }
      }
    }
  },
  { immediate: true },
)

function onFieldUpdate({ field, value }) {
  form[field] = value
}

function onRegisterSignaturePad(pad) {
  signaturePadRef.value = pad
}

function totalVotesAvailable() {
  const shares = form.signcustodian
    ? Number(form.custodianShares || 0)
    : Number(props.shareholder.shares || 0)
  return shares * props.shareValue
}

function directedSum(questionId) {
  const d = form.answers[questionId]?.directed || {}
  return Object.values(d).reduce((sum, v) => sum + (parseFloat(v) || 0), 0)
}

function toastWarn(msg) {
  toast.add({ severity: 'warn', summary: '', detail: msg, life: 3500 })
}
function toastErr(msg) {
  toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 3500 })
}

async function submit() {
  if (props.mode !== 'vote') return

  // Validate every ballot question has a choice, and if directed, the sum
  // matches the available votes.
  const questions = (props.ballot.components || []).filter(
    (c) => c.type === 'ballotQuestion',
  )
  for (const q of questions) {
    const ans = form.answers[q.id]
    if (!ans?.choice) {
      return toastWarn(`Please choose A or B for "${q.props?.title || q.id}".`)
    }
    if (ans.choice === 'b') {
      const sum = directedSum(q.id)
      const available = totalVotesAvailable()
      if (sum > available) {
        return toastWarn(`You used more votes than available for "${q.props?.title || q.id}".`)
      }
      if (sum < available) {
        return toastWarn(`You did not use all of your votes for "${q.props?.title || q.id}".`)
      }
    }
  }

  // Signature
  const hasSignatureBlock = props.ballot.components.some(
    (c) => c.type === 'signature',
  )
  if (hasSignatureBlock) {
    const pad = signaturePadRef.value
    if (!pad || pad.isEmpty()) {
      return toastErr('Your signature is required.')
    }
    form.signatureDataUrl = pad.toDataURL('image/png')
  }

  if (form.signcustodian && !form.custodian) {
    return toastErr('A custodian name is required.')
  }
  if (form.sigx && (!form.witness1 || !form.witness2)) {
    return toastErr('Two witness names are required if signing with an "X".')
  }

  const payload = {
    answers: JSON.parse(JSON.stringify(form.answers)),
    signcustodian: !!form.signcustodian,
    custodian: form.custodian || null,
    custodianshn: form.custodianshn || null,
    totalshares: form.signcustodian
      ? Number(form.custodianShares || 0)
      : Number(props.shareholder.shares || 0),
    sigx: !!form.sigx,
    witness1: form.witness1 || null,
    witness2: form.witness2 || null,
    signatureDataUrl: form.signatureDataUrl,
  }

  submitting.value = true
  try {
    if (typeof props.onSubmit === 'function') {
      await props.onSubmit(payload)
    } else {
      // Back-compat: listeners bound via @submit still fire, but they can't
      // extend the loading state since emit doesn't await.
      emit('submit', payload)
    }
  } catch (err) {
    // The parent handler may have already surfaced the error; log for safety.
    console.error('ballot submit error', err)
  } finally {
    submitting.value = false
  }
}

defineExpose({ submit, form })

const totals = computed(() => ({ totalVotesAvailable: totalVotesAvailable() }))
// expose totals to view components via provide/inject-lite: we re-emit on form
// changes instead of a heavy reactive dependency — each view gets the form
// reference directly as a prop.
</script>

<style lang="scss" scoped>
.ballot-renderer {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .submit-row {
    margin-top: 24px;
    text-align: center;
  }
}

.ballot-empty {
  padding: 40px;
  text-align: center;
  color: #777;
}
</style>
